import { useAtom, useAtomValue } from "jotai";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "src/i18n";
import { useModals } from "src/components/Modals";
import { useToasts } from "src/components/Toasts/store/hooks";
import {
  Button,
  Color,
  Flex,
  HorizontalDivider,
  Modal,
  Scroller,
  Tabs,
  Text,
  Toggle,
} from "src/elements";
import {
  DEFAULT_WEIGHT_RULES,
  defaultPspRules,
} from "src/pages/Merchflows_merchflowId_BasePog_basePogId_/store/types";
import {
  exportPspRulesConfig,
  parsePspRulesTemplate,
  type PspRulesFromFile,
} from "./store/configTemplate";
import { VerticalBlockingTunables, verticalBlockingPreset } from "./store/verticalBlockingPreset";
import { downloadFile } from "src/utils/downloadFile";
import { styledSafe } from "src/utils/styledComponent";
import { pages } from "src/utils";
import { useNavigation } from "src/utils/navigation";
import {
  storePlanogramRulesDataAtom,
  StorePlanogramRulesModalBaseProps,
  StorePlanogramRulesModalProps,
  StorePlanogramRulesModalStoreProps,
} from "./store/atoms";
import { useApi } from "src/api";
import { PlanogramOptimizeOn } from "src/components";
import { GeneralPlanogramRules } from "src/components/PlanogramRules/GeneralPlanogramRules";
import { UhdPlanogramRules } from "src/components/PlanogramRules/UhdPlanogramRules";
import { OptimizationOptionsRules } from "src/components/PlanogramRules/OptimizationOptionsRules";
import { MudmapOptimizationOptions } from "src/components/PlanogramRules/MudmapOptimizationOptions";
import { WeightRules } from "src/components/PlanogramRules/WeightRules";
import { merchantSettingsAtom } from "src/components/FeatureFlagProvider/store/atoms";
import { PlanogramMudmapLevel } from "../../components/PlanogramRules/components/PlanogramMudmapLevel";
import { PspRules } from "@CommonTypes/backend/PspRules";
import { routeToUrl } from "@CommonUtils/routes";
import { hasPermission, PermissionCode } from "@CommonUtils/RolesPermissions";
import { useUser } from "src/atoms/user";

const HiddenInput = styledSafe("input")`
  display: none;
`;

const STORE_GENERATION_POLL_INTERVAL_MS = 3000;
const MAX_STORE_GENERATION_POLL_ATTEMPTS = 60;
type RulesTab = "GENERAL" | "UHD" | "OPTIMIZATION" | "WEIGHT";
type RulesTabOption = { label: string; value: RulesTab };

const wait = (duration: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, duration);
  });

// A bar above the tabs that says something is off and leaves the choice open.
const Notice = ({ text, children }: { text: string; children: ReactNode }) => (
  <Flex
    background={Color.lightGray}
    border={`1px solid ${Color.lightGrayHover}`}
    borderRadius="3px"
    padding="10px"
    gap="10px"
    align="center"
    justify="between"
  >
    <Text variant="small1">{text}</Text>

    <Flex gap="8px">{children}</Flex>
  </Flex>
);

export const ModalStorePlanogramRules = () => {
  const [data, setData] = useAtom(storePlanogramRulesDataAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, []);

  if (!data) return null;

  return <ModalStorePlanogramRulesInner {...data} />;
};

const ModalStorePlanogramRulesInner = (props: StorePlanogramRulesModalProps) => {
  const { merchflowId, mode } = props;
  const defaultHighDensityEnabled =
    props.mode === "store" ? props.defaultHighDensityEnabled : undefined;
  const { closeModal: _closeModal, openConfirmModal } = useModals();
  const { t } = useTranslation("common");
  const { toast } = useToasts();
  const { navigate } = useNavigation();
  const { user } = useUser();
  const canCreate = hasPermission(user.roleCode, PermissionCode.MERCHFLOW_CREATE);
  const {
    postMerchflowBasePog_basePogId_StatusApi,
    postMerchflowStartStorePlanogramsGenerationApi,
    postMerchflowStartStorePlanogramsCopyToStoresGenerationApi,
    getMerchflowStoresAllApi,
    postStartMerchflowBasePogApi,
  } = useApi();

  const merchantSettings = useAtomValue(merchantSettingsAtom);
  // Same gate the backend applies: with the merchant setting off it sends no
  // weight at all, so a tab of rules nothing reads would be a lie.
  const isShelfWeightEnabled = merchantSettings?.shelfWeightEnabled ?? false;
  const [tab, setTab] = useState<RulesTab>("GENERAL");
  const [optimizeOn, setOptimizeOn] = useState<PspRules["optimize_on"]>("SALES");
  const [mudmapLevel, setMudmapLevel] = useState<PspRules["mudmap_level"]>(
    defaultPspRules.mudmap_level,
  );
  const [copyToStores, setCopyToStores] = useState(false);
  const [showEngineNeededNotice, setShowEngineNeededNotice] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const isGeneratingRef = useRef(false);
  const isMountedRef = useRef(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // GENERAL - Fixtures
  const [canAdjustShelfNotches, setCanAdjustShelfNotches] = useState(
    defaultPspRules.fixture.can_adjust_shelf_notches,
  );
  const [canAddOrDeleteShelves, setCanAddOrDeleteShelves] = useState(
    defaultPspRules.fixture.can_add_or_delete_shelves,
  );
  const [shelvesSameHeightAsBasePog, setShelvesSameHeightAsBasePog] = useState(
    defaultPspRules.fixture.shelves_same_height_as_base_pog,
  );
  const [verticalOverlap, setVerticalOverlap] = useState(defaultPspRules.fixture.vertical_overlap);
  const [mergedShelfWidthTolerancePct, setMergedShelfWidthTolerancePct] = useState(
    defaultPspRules.fixture.merged_shelf_width_tolerance_pct,
  );

  // GENERAL - Range
  const [canAddNonCoreRangeProducts, setCanAddNonCoreRangeProducts] = useState(
    defaultPspRules.range.can_add_non_core_range_products,
  );
  const [canApplySqueezeToPlacedProducts, setCanApplySqueezeToPlacedProducts] = useState(
    defaultPspRules.range.can_apply_squeeze_to_placed_products,
  );

  // GENERAL - Merchandising
  const [verticalBlockingMode, setVerticalBlockingMode] = useState(
    defaultPspRules.merchandising.vertical_blocking_mode,
  );
  const [canBreakCasesTraysIfCoreRangeNotFit, setCanBreakCasesTraysIfCoreRangeNotFit] = useState(
    defaultPspRules.merchandising.can_break_cases_trays_if_core_range_not_fit,
  );
  const [
    canFillExtraSpaceNextToCasesTraysWithLooseProducts,
    setCanFillExtraSpaceNextToCasesTraysWithLooseProducts,
  ] = useState(
    defaultPspRules.merchandising.can_fill_extra_space_next_to_cases_trays_with_loose_products,
  );
  const [
    howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
    setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
  ] = useState(
    defaultPspRules.merchandising
      .how_should_loose_products_be_placed_with_respect_to_their_cases_trays,
  );
  const [leverageAlternativeOrientations, setLeverageAlternativeOrientations] = useState(
    defaultPspRules.merchandising.leverage_alternative_orientations,
  );
  const [extraFacingMarginalValue, setExtraFacingMarginalValue] = useState<number>(
    defaultPspRules.merchandising.extra_facing_marginal_value ?? 0,
  );
  const [snaking, setSnaking] = useState(defaultPspRules.merchandising.snaking);
  const [cdtStacking, setCdtStacking] = useState(defaultPspRules.merchandising.cdt_stacking);
  const [itemsSqueezeOption, setItemsSqueezeOption] = useState<
    PspRules["merchandising"]["items_squeeze_option"]
  >(defaultPspRules.merchandising.items_squeeze_option);

  // UHD MODE - High Density Normal
  const [highDensityEnabled, setHighDensityEnabled] = useState(
    defaultHighDensityEnabled ?? defaultPspRules.high_density_mode.enabled,
  );
  const [placeAllVariants, setPlaceAllVariants] = useState(
    defaultPspRules.high_density_mode.place_all_variants,
  );
  const [snakingStrategy, setSnakingStrategy] = useState<
    PspRules["high_density_mode"]["snaking_strategy"]
  >(defaultPspRules.high_density_mode.snaking_strategy);
  const [variantContiguous, setVariantContiguous] = useState(
    defaultPspRules.high_density_mode.variant_contiguous,
  );
  const [allowBreakingVariantSequencing, setAllowBreakingVariantSequencing] = useState(
    defaultPspRules.high_density_mode.allow_breaking_variant_sequencing,
  );
  const [ameVerticalBlockingMode, setAmeVerticalBlockingMode] = useState(
    defaultPspRules.high_density_mode.vertical_blocking_mode,
  );
  const [strictRectangles, setStrictRectangles] = useState(
    defaultPspRules.high_density_mode.strict_rectangles,
  );
  const [verticalSplitSection, setVerticalSplitSection] = useState(
    defaultPspRules.fixture.vertical_split_section,
  );

  // UHD MODE - UHD Building Blocks
  // High Density Building Blocks Mode
  const [buildingBlocksEnabled, setBuildingBlocksEnabled] = useState(
    defaultPspRules.high_density_building_blocks_mode.enabled,
  );
  const [buildingBlocksDirection, setBuildingBlocksDirection] = useState<
    PspRules["high_density_building_blocks_mode"]["direction"]
  >(defaultPspRules.high_density_building_blocks_mode.direction);
  const [startFromType, setStartFromType] = useState<
    PspRules["high_density_building_blocks_mode"]["start_from"]
  >(defaultPspRules.high_density_building_blocks_mode.start_from);

  // OPTIMIZATION OPTIONS
  const [spaceMix, setSpaceMix] = useState(defaultPspRules.relax_factors.space_mix);
  const [eitherOr, setEitherOr] = useState(defaultPspRules.relax_factors.either_or);
  const [bothOrNone, setBothOrNone] = useState(defaultPspRules.relax_factors.both_or_none);
  const [preReq, setPreReq] = useState(defaultPspRules.relax_factors.prereq);
  const [shelfItemCount, setShelfItemCount] = useState(
    defaultPspRules.relax_factors.shelf_item_count,
  );
  const [fixedNotchNum, setFixedNotchNum] = useState(defaultPspRules.relax_factors.fixed_notch_num);
  const [fingerSpace, setFingerSpace] = useState(defaultPspRules.relax_factors.finger_space);
  const [unitsNotEnoughDos, setUnitsNotEnoughDos] = useState(
    defaultPspRules.relax_factors.units_not_enough_dos,
  );
  const [unitsNotEnoughMos, setUnitsNotEnoughMos] = useState(
    defaultPspRules.relax_factors.units_not_enough_mos,
  );
  const [unitsTooMany, setUnitsTooMany] = useState(defaultPspRules.relax_factors.units_too_many);
  const [facingsNotEnough, setFacingsNotEnough] = useState(
    defaultPspRules.relax_factors.facings_not_enough,
  );
  const [facingsTooMany, setFacingsTooMany] = useState(
    defaultPspRules.relax_factors.facings_too_many,
  );
  const [rowsNotEnough, setRowsNotEnough] = useState(defaultPspRules.relax_factors.rows_not_enough);
  const [minWidth, setMinWidth] = useState(defaultPspRules.relax_factors.min_width);
  const [minDistribution, setMinDistribution] = useState(
    defaultPspRules.relax_factors.min_distribution,
  );
  const [widthSlack, setWidthSlack] = useState(defaultPspRules.relax_factors.width_slack);
  const [cdt1Contiguous, setCdt1Contiguous] = useState(
    defaultPspRules.relax_factors.cdt1_contiguous,
  );
  const [cdt2Contiguous, setCdt2Contiguous] = useState(
    defaultPspRules.relax_factors.cdt2_contiguous,
  );
  const [cdt3Contiguous, setCdt3Contiguous] = useState(
    defaultPspRules.relax_factors.cdt3_contiguous,
  );
  const [cdt1Order, setCdt1Order] = useState(defaultPspRules.relax_factors.cdt1_order);
  const [cdt2Order, setCdt2Order] = useState(defaultPspRules.relax_factors.cdt2_order);
  const [cdt3Order, setCdt3Order] = useState(defaultPspRules.relax_factors.cdt3_order);
  const [itemIdOrder, setItemIdOrder] = useState(defaultPspRules.relax_factors.item_id_order);
  const [itemSpan, setItemSpan] = useState(defaultPspRules.relax_factors.item_span);
  const [cdt1Span, setCdt1Span] = useState(defaultPspRules.relax_factors.cdt1_span);
  const [cdt2Span, setCdt2Span] = useState(defaultPspRules.relax_factors.cdt2_span);
  const [cdt3Span, setCdt3Span] = useState(defaultPspRules.relax_factors.cdt3_span);
  const [secondaryOrientation, setSecondaryOrientation] = useState(
    defaultPspRules.relax_factors.secondary_orientation,
  );
  const [shelfSegmentSpace, setShelfSegmentSpace] = useState(
    defaultPspRules.relax_factors.shelf_segment_space,
  );

  // WEIGHT RULES
  const [defaultShelfWeightLimit, setDefaultShelfWeightLimit] = useState(
    DEFAULT_WEIGHT_RULES.default_shelf_weight_limit,
  );
  const [heavyRule, setHeavyRule] = useState(DEFAULT_WEIGHT_RULES.heavy_rule);
  const [resolveOverweightShelves, setResolveOverweightShelves] = useState(
    DEFAULT_WEIGHT_RULES.resolve_overweight_shelves,
  );
  const [swapHeavyBrandsBelowMaxHeight, setSwapHeavyBrandsBelowMaxHeight] = useState(
    DEFAULT_WEIGHT_RULES.swap_heavy_brands_below_max_height,
  );
  const [heavyBrandBlockLevel, setHeavyBrandBlockLevel] = useState(
    DEFAULT_WEIGHT_RULES.heavy_brand_block_level,
  );
  const [swapFillFloorPct, setSwapFillFloorPct] = useState(
    DEFAULT_WEIGHT_RULES.swap_fill_floor_pct,
  );

  // MUDMAP OPTIMIZATION OPTIONS
  const mudmapPenaltyDefaults: NonNullable<PspRules["mudmap_generation_penalty_multiplier"]> =
    defaultPspRules.mudmap_generation_penalty_multiplier ?? {
      totalSpaceSlack: 0,
      cdt0SectionsSpanning: 0,
      cdt0ShelvesSpanning: 0,
      cdt2SectionsSpanning: 0,
      cdt2ShelvesSpanning: 0,
      cdt0RoundingSlack: 0,
      cdt2RoundingSlack: 0,
      cdt1BreakingSequencing: 0,
      cdt2BreakingSequencing: 0,
      cdtSpaceMixSlack: 0,
      cdt0ShelvesMisaligned: 0,
      itemWidthSatisfied: 0,
      cdt0ShelfSpaceMisalignment: 0,
      cdt2ShelfSpaceMisalignment: 0,
      verticalBlockShelfPresence: 0,
      cdt0CellXMisalignment: 0,
      cdt2CellXMisalignment: 0,
    };

  const [mudmapOptimizationEnabled, setMudmapOptimizationEnabled] = useState(true);
  const [totalSpaceSlack, setTotalSpaceSlack] = useState(mudmapPenaltyDefaults.totalSpaceSlack);
  const [cdt0SectionsSpanning, setCdt0SectionsSpanning] = useState(
    mudmapPenaltyDefaults.cdt0SectionsSpanning,
  );
  const [cdt0ShelvesSpanning, setCdt0ShelvesSpanning] = useState(
    mudmapPenaltyDefaults.cdt0ShelvesSpanning,
  );
  const [cdt2SectionsSpanning, setCdt2SectionsSpanning] = useState(
    mudmapPenaltyDefaults.cdt2SectionsSpanning,
  );
  const [cdt2ShelvesSpanning, setCdt2ShelvesSpanning] = useState(
    mudmapPenaltyDefaults.cdt2ShelvesSpanning,
  );
  const [cdt0RoundingSlack, setCdt0RoundingSlack] = useState(
    mudmapPenaltyDefaults.cdt0RoundingSlack,
  );
  const [cdt2RoundingSlack, setCdt2RoundingSlack] = useState(
    mudmapPenaltyDefaults.cdt2RoundingSlack,
  );
  const [cdt1BreakingSequencing, setCdt1BreakingSequencing] = useState(
    mudmapPenaltyDefaults.cdt1BreakingSequencing,
  );
  const [cdt2BreakingSequencing, setCdt2BreakingSequencing] = useState(
    mudmapPenaltyDefaults.cdt2BreakingSequencing,
  );
  const [cdtSpaceMixSlack, setCdtSpaceMixSlack] = useState(mudmapPenaltyDefaults.cdtSpaceMixSlack);
  const [cdt0ShelvesMisaligned, setCdt0ShelvesMisaligned] = useState(
    mudmapPenaltyDefaults.cdt0ShelvesMisaligned,
  );
  const [itemWidthSatisfied, setItemWidthSatisfied] = useState(
    mudmapPenaltyDefaults.itemWidthSatisfied,
  );
  const [cdt0ShelfSpaceMisalignment, setCdt0ShelfSpaceMisalignment] = useState(
    mudmapPenaltyDefaults.cdt0ShelfSpaceMisalignment,
  );
  const [cdt2ShelfSpaceMisalignment, setCdt2ShelfSpaceMisalignment] = useState(
    mudmapPenaltyDefaults.cdt2ShelfSpaceMisalignment,
  );
  const [verticalBlockShelfPresence, setVerticalBlockShelfPresence] = useState(
    mudmapPenaltyDefaults.verticalBlockShelfPresence,
  );
  const [cdt0CellXMisalignment, setCdt0CellXMisalignment] = useState(
    mudmapPenaltyDefaults.cdt0CellXMisalignment,
  );
  const [cdt2CellXMisalignment, setCdt2CellXMisalignment] = useState(
    mudmapPenaltyDefaults.cdt2CellXMisalignment,
  );

  const resetMudmapOptimizationOptions = () => {
    setMudmapOptimizationEnabled(true);
    setTotalSpaceSlack(mudmapPenaltyDefaults.totalSpaceSlack);
    setCdt0SectionsSpanning(mudmapPenaltyDefaults.cdt0SectionsSpanning);
    setCdt0ShelvesSpanning(mudmapPenaltyDefaults.cdt0ShelvesSpanning);
    setCdt2SectionsSpanning(mudmapPenaltyDefaults.cdt2SectionsSpanning);
    setCdt2ShelvesSpanning(mudmapPenaltyDefaults.cdt2ShelvesSpanning);
    setCdt0RoundingSlack(mudmapPenaltyDefaults.cdt0RoundingSlack);
    setCdt2RoundingSlack(mudmapPenaltyDefaults.cdt2RoundingSlack);
    setCdt1BreakingSequencing(mudmapPenaltyDefaults.cdt1BreakingSequencing);
    setCdt2BreakingSequencing(mudmapPenaltyDefaults.cdt2BreakingSequencing);
    setCdtSpaceMixSlack(mudmapPenaltyDefaults.cdtSpaceMixSlack);
    setCdt0ShelvesMisaligned(mudmapPenaltyDefaults.cdt0ShelvesMisaligned);
    setItemWidthSatisfied(mudmapPenaltyDefaults.itemWidthSatisfied);
    setCdt0ShelfSpaceMisalignment(mudmapPenaltyDefaults.cdt0ShelfSpaceMisalignment);
    setCdt2ShelfSpaceMisalignment(mudmapPenaltyDefaults.cdt2ShelfSpaceMisalignment);
    setVerticalBlockShelfPresence(mudmapPenaltyDefaults.verticalBlockShelfPresence);
    setCdt0CellXMisalignment(mudmapPenaltyDefaults.cdt0CellXMisalignment);
    setCdt2CellXMisalignment(mudmapPenaltyDefaults.cdt2CellXMisalignment);
  };

  // The preset is a starting point: every value stays editable afterwards.
  const applyVerticalBlockingTunables = (tunables: VerticalBlockingTunables) => {
    setMudmapLevel(tunables.mudmapLevel);
    setExtraFacingMarginalValue(tunables.extraFacingMarginalValue);
    setPlaceAllVariants(tunables.placeAllVariants);
    setSnakingStrategy(tunables.snakingStrategy);
    setSnaking(tunables.snaking);
    setCdtStacking(tunables.cdtStacking);
    setFixedNotchNum(tunables.fixedNotchNum);
    setSpaceMix(tunables.spaceMix);
    setUnitsNotEnoughDos(tunables.unitsNotEnoughDos);
    setUnitsNotEnoughMos(tunables.unitsNotEnoughMos);
    setFacingsTooMany(tunables.facingsTooMany);
    setMinDistribution(tunables.minDistribution);
    setShelfSegmentSpace(tunables.shelfSegmentSpace);
    setCdt1Contiguous(tunables.cdt1Contiguous);
    setCdt2Contiguous(tunables.cdt2Contiguous);
    setCdt3Contiguous(tunables.cdt3Contiguous);
    setCdt1Order(tunables.cdt1Order);
    setCdt2Order(tunables.cdt2Order);
    setCdt3Order(tunables.cdt3Order);
    setItemIdOrder(tunables.itemIdOrder);
    setItemSpan(tunables.itemSpan);
    setCdt0ShelvesMisaligned(tunables.cdt0ShelvesMisaligned);
    setCdt0ShelfSpaceMisalignment(tunables.cdt0ShelfSpaceMisalignment);
    setCdt2ShelfSpaceMisalignment(tunables.cdt2ShelfSpaceMisalignment);
    setCdt0CellXMisalignment(tunables.cdt0CellXMisalignment);
    setCdt2CellXMisalignment(tunables.cdt2CellXMisalignment);
  };

  // One switch for base and store generation alike: the core flag, the mudmap
  // flag, the tested preset and the engine. Base generation posts the same
  // psp_rules as store generation, so a core-only switch there would send
  // vertical blocking with the production defaults it was never tested with.
  const handleVerticalBlockingModeChange = (isOn: boolean) => {
    setVerticalBlockingMode(isOn);
    setAmeVerticalBlockingMode(isOn);
    if (isOn) {
      applyVerticalBlockingTunables(verticalBlockingPreset);
      setHighDensityEnabled(true);
    }

    setShowEngineNeededNotice(false);
  };

  // Vertical blocking remains enabled when Auto-Mudmap is off, so leave the user
  // an informational choice without blocking generation.
  const handleHighDensityEnabledChange = (isOn: boolean) => {
    setHighDensityEnabled(isOn);
    setShowEngineNeededNotice(!isOn && (verticalBlockingMode || ameVerticalBlockingMode));
  };

  const applyPspRules = (rules: PspRulesFromFile) => {
    const importedVerticalBlockingMode =
      rules.merchandising.vertical_blocking_mode ??
      defaultPspRules.merchandising.vertical_blocking_mode;
    const importedAmeVerticalBlockingMode =
      rules.high_density_mode.vertical_blocking_mode ??
      defaultPspRules.high_density_mode.vertical_blocking_mode;

    // `default_shelf_weight_limit` and `heavy_rule` are both meaningfully
    // nullable: `?? default` would resurrect a cap or a rule the imported
    // config deliberately turned off.
    setDefaultShelfWeightLimit(
      rules.weight_rules
        ? rules.weight_rules.default_shelf_weight_limit
        : DEFAULT_WEIGHT_RULES.default_shelf_weight_limit,
    );
    setHeavyRule(
      rules.weight_rules ? rules.weight_rules.heavy_rule : DEFAULT_WEIGHT_RULES.heavy_rule,
    );
    setResolveOverweightShelves(
      rules.weight_rules?.resolve_overweight_shelves ??
        DEFAULT_WEIGHT_RULES.resolve_overweight_shelves,
    );
    setSwapHeavyBrandsBelowMaxHeight(
      rules.weight_rules?.swap_heavy_brands_below_max_height ??
        DEFAULT_WEIGHT_RULES.swap_heavy_brands_below_max_height,
    );
    setHeavyBrandBlockLevel(
      rules.weight_rules?.heavy_brand_block_level ?? DEFAULT_WEIGHT_RULES.heavy_brand_block_level,
    );
    setSwapFillFloorPct(
      rules.weight_rules?.swap_fill_floor_pct ?? DEFAULT_WEIGHT_RULES.swap_fill_floor_pct,
    );
    setCanAdjustShelfNotches(
      rules.fixture.can_adjust_shelf_notches ?? defaultPspRules.fixture.can_adjust_shelf_notches,
    );
    setCanAddOrDeleteShelves(
      rules.fixture.can_add_or_delete_shelves ?? defaultPspRules.fixture.can_add_or_delete_shelves,
    );
    setShelvesSameHeightAsBasePog(
      rules.fixture.shelves_same_height_as_base_pog ??
        defaultPspRules.fixture.shelves_same_height_as_base_pog,
    );
    setVerticalOverlap(rules.fixture.vertical_overlap ?? defaultPspRules.fixture.vertical_overlap);
    setMergedShelfWidthTolerancePct(
      rules.fixture.merged_shelf_width_tolerance_pct ??
        defaultPspRules.fixture.merged_shelf_width_tolerance_pct,
    );
    setVerticalSplitSection(
      rules.fixture.vertical_split_section ?? defaultPspRules.fixture.vertical_split_section,
    );

    setCanAddNonCoreRangeProducts(
      rules.range.can_add_non_core_range_products ??
        defaultPspRules.range.can_add_non_core_range_products,
    );
    setCanApplySqueezeToPlacedProducts(
      rules.range.can_apply_squeeze_to_placed_products ??
        defaultPspRules.range.can_apply_squeeze_to_placed_products,
    );

    setVerticalBlockingMode(importedVerticalBlockingMode);
    setCanBreakCasesTraysIfCoreRangeNotFit(
      rules.merchandising.can_break_cases_trays_if_core_range_not_fit ??
        defaultPspRules.merchandising.can_break_cases_trays_if_core_range_not_fit,
    );
    setCanFillExtraSpaceNextToCasesTraysWithLooseProducts(
      rules.merchandising.can_fill_extra_space_next_to_cases_trays_with_loose_products ??
        defaultPspRules.merchandising.can_fill_extra_space_next_to_cases_trays_with_loose_products,
    );
    setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays(
      rules.merchandising.how_should_loose_products_be_placed_with_respect_to_their_cases_trays ??
        defaultPspRules.merchandising
          .how_should_loose_products_be_placed_with_respect_to_their_cases_trays,
    );
    setLeverageAlternativeOrientations(
      rules.merchandising.leverage_alternative_orientations ??
        defaultPspRules.merchandising.leverage_alternative_orientations,
    );
    setExtraFacingMarginalValue(
      rules.merchandising.extra_facing_marginal_value ??
        defaultPspRules.merchandising.extra_facing_marginal_value ??
        0,
    );
    setSnaking(rules.merchandising.snaking ?? defaultPspRules.merchandising.snaking);
    setCdtStacking(rules.merchandising.cdt_stacking ?? defaultPspRules.merchandising.cdt_stacking);
    setItemsSqueezeOption(
      rules.merchandising.items_squeeze_option ??
        defaultPspRules.merchandising.items_squeeze_option,
    );

    // Building blocks is an additional mode on top of the engine, so configs
    // with building blocks enabled must keep the engine enabled as well.
    const engineEnabled =
      (rules.high_density_mode.enabled ?? defaultPspRules.high_density_mode.enabled) ||
      (rules.high_density_building_blocks_mode.enabled ??
        defaultPspRules.high_density_building_blocks_mode.enabled);
    setHighDensityEnabled(engineEnabled);
    setPlaceAllVariants(
      rules.high_density_mode.place_all_variants ??
        defaultPspRules.high_density_mode.place_all_variants,
    );
    setSnakingStrategy(
      rules.high_density_mode.snaking_strategy ??
        defaultPspRules.high_density_mode.snaking_strategy,
    );
    setVariantContiguous(
      rules.high_density_mode.variant_contiguous ??
        defaultPspRules.high_density_mode.variant_contiguous,
    );
    setAllowBreakingVariantSequencing(
      rules.high_density_mode.allow_breaking_variant_sequencing ??
        defaultPspRules.high_density_mode.allow_breaking_variant_sequencing,
    );
    setAmeVerticalBlockingMode(importedAmeVerticalBlockingMode);
    setStrictRectangles(
      rules.high_density_mode.strict_rectangles ??
        defaultPspRules.high_density_mode.strict_rectangles,
    );

    setBuildingBlocksEnabled(
      rules.high_density_building_blocks_mode.enabled ??
        defaultPspRules.high_density_building_blocks_mode.enabled,
    );
    setBuildingBlocksDirection(
      rules.high_density_building_blocks_mode.direction ??
        defaultPspRules.high_density_building_blocks_mode.direction,
    );
    setStartFromType(
      rules.high_density_building_blocks_mode.start_from ??
        defaultPspRules.high_density_building_blocks_mode.start_from,
    );

    setMudmapLevel(rules.mudmap_level ?? defaultPspRules.mudmap_level);
    setOptimizeOn(rules.optimize_on ?? defaultPspRules.optimize_on);

    setSpaceMix(rules.relax_factors.space_mix ?? defaultPspRules.relax_factors.space_mix);
    setEitherOr(rules.relax_factors.either_or ?? defaultPspRules.relax_factors.either_or);
    setBothOrNone(rules.relax_factors.both_or_none ?? defaultPspRules.relax_factors.both_or_none);
    setPreReq(rules.relax_factors.prereq ?? defaultPspRules.relax_factors.prereq);
    setShelfItemCount(
      rules.relax_factors.shelf_item_count ?? defaultPspRules.relax_factors.shelf_item_count,
    );
    setFixedNotchNum(
      rules.relax_factors.fixed_notch_num ?? defaultPspRules.relax_factors.fixed_notch_num,
    );
    setFingerSpace(rules.relax_factors.finger_space ?? defaultPspRules.relax_factors.finger_space);
    setUnitsNotEnoughDos(
      rules.relax_factors.units_not_enough_dos ??
        defaultPspRules.relax_factors.units_not_enough_dos,
    );
    setUnitsNotEnoughMos(
      rules.relax_factors.units_not_enough_mos ??
        defaultPspRules.relax_factors.units_not_enough_mos,
    );
    setUnitsTooMany(
      rules.relax_factors.units_too_many ?? defaultPspRules.relax_factors.units_too_many,
    );
    setFacingsNotEnough(
      rules.relax_factors.facings_not_enough ?? defaultPspRules.relax_factors.facings_not_enough,
    );
    setFacingsTooMany(
      rules.relax_factors.facings_too_many ?? defaultPspRules.relax_factors.facings_too_many,
    );
    setRowsNotEnough(
      rules.relax_factors.rows_not_enough ?? defaultPspRules.relax_factors.rows_not_enough,
    );
    setMinWidth(rules.relax_factors.min_width ?? defaultPspRules.relax_factors.min_width);
    setMinDistribution(
      rules.relax_factors.min_distribution ?? defaultPspRules.relax_factors.min_distribution,
    );
    setWidthSlack(rules.relax_factors.width_slack ?? defaultPspRules.relax_factors.width_slack);
    setCdt1Contiguous(
      rules.relax_factors.cdt1_contiguous ?? defaultPspRules.relax_factors.cdt1_contiguous,
    );
    setCdt2Contiguous(
      rules.relax_factors.cdt2_contiguous ?? defaultPspRules.relax_factors.cdt2_contiguous,
    );
    setCdt3Contiguous(
      rules.relax_factors.cdt3_contiguous ?? defaultPspRules.relax_factors.cdt3_contiguous,
    );
    setCdt1Order(rules.relax_factors.cdt1_order ?? defaultPspRules.relax_factors.cdt1_order);
    setCdt2Order(rules.relax_factors.cdt2_order ?? defaultPspRules.relax_factors.cdt2_order);
    setCdt3Order(rules.relax_factors.cdt3_order ?? defaultPspRules.relax_factors.cdt3_order);
    setItemIdOrder(
      rules.relax_factors.item_id_order ?? defaultPspRules.relax_factors.item_id_order,
    );
    setItemSpan(rules.relax_factors.item_span ?? defaultPspRules.relax_factors.item_span);
    setCdt1Span(rules.relax_factors.cdt1_span ?? defaultPspRules.relax_factors.cdt1_span);
    setCdt2Span(rules.relax_factors.cdt2_span ?? defaultPspRules.relax_factors.cdt2_span);
    setCdt3Span(rules.relax_factors.cdt3_span ?? defaultPspRules.relax_factors.cdt3_span);
    setSecondaryOrientation(
      rules.relax_factors.secondary_orientation ??
        defaultPspRules.relax_factors.secondary_orientation,
    );
    setShelfSegmentSpace(
      rules.relax_factors.shelf_segment_space ?? defaultPspRules.relax_factors.shelf_segment_space,
    );

    const penalty = rules.mudmap_generation_penalty_multiplier ?? mudmapPenaltyDefaults;
    setTotalSpaceSlack(penalty.totalSpaceSlack ?? mudmapPenaltyDefaults.totalSpaceSlack);
    setCdt0SectionsSpanning(
      penalty.cdt0SectionsSpanning ?? mudmapPenaltyDefaults.cdt0SectionsSpanning,
    );
    setCdt0ShelvesSpanning(
      penalty.cdt0ShelvesSpanning ?? mudmapPenaltyDefaults.cdt0ShelvesSpanning,
    );
    setCdt2SectionsSpanning(
      penalty.cdt2SectionsSpanning ?? mudmapPenaltyDefaults.cdt2SectionsSpanning,
    );
    setCdt2ShelvesSpanning(
      penalty.cdt2ShelvesSpanning ?? mudmapPenaltyDefaults.cdt2ShelvesSpanning,
    );
    setCdt0RoundingSlack(penalty.cdt0RoundingSlack ?? mudmapPenaltyDefaults.cdt0RoundingSlack);
    setCdt2RoundingSlack(penalty.cdt2RoundingSlack ?? mudmapPenaltyDefaults.cdt2RoundingSlack);
    setCdt1BreakingSequencing(
      penalty.cdt1BreakingSequencing ?? mudmapPenaltyDefaults.cdt1BreakingSequencing,
    );
    setCdt2BreakingSequencing(
      penalty.cdt2BreakingSequencing ?? mudmapPenaltyDefaults.cdt2BreakingSequencing,
    );
    setCdtSpaceMixSlack(penalty.cdtSpaceMixSlack ?? mudmapPenaltyDefaults.cdtSpaceMixSlack);
    setCdt0ShelvesMisaligned(
      penalty.cdt0ShelvesMisaligned ?? mudmapPenaltyDefaults.cdt0ShelvesMisaligned,
    );
    setItemWidthSatisfied(penalty.itemWidthSatisfied ?? mudmapPenaltyDefaults.itemWidthSatisfied);
    setCdt0ShelfSpaceMisalignment(
      penalty.cdt0ShelfSpaceMisalignment ?? mudmapPenaltyDefaults.cdt0ShelfSpaceMisalignment,
    );
    setCdt2ShelfSpaceMisalignment(
      penalty.cdt2ShelfSpaceMisalignment ?? mudmapPenaltyDefaults.cdt2ShelfSpaceMisalignment,
    );
    setVerticalBlockShelfPresence(
      penalty.verticalBlockShelfPresence ?? mudmapPenaltyDefaults.verticalBlockShelfPresence,
    );
    setCdt0CellXMisalignment(
      penalty.cdt0CellXMisalignment ?? mudmapPenaltyDefaults.cdt0CellXMisalignment,
    );
    setCdt2CellXMisalignment(
      penalty.cdt2CellXMisalignment ?? mudmapPenaltyDefaults.cdt2CellXMisalignment,
    );

    setShowEngineNeededNotice(
      mode === "store" &&
        (importedVerticalBlockingMode || importedAmeVerticalBlockingMode) &&
        !engineEnabled,
    );
  };

  const handleExportConfig = () => {
    downloadFile({
      name: "planogram-config",
      extension: "json",
      data: exportPspRulesConfig(buildPspRules()),
    });
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportConfig = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const rules = await parsePspRulesTemplate(file);
      applyPspRules(rules);
      toast({ title: "Configuration imported successfully" });
    } catch (error) {
      toast({ title: "Invalid configuration file", error });
    }
  };

  const buildPspRules = (): PspRules => {
    return {
      fixture: {
        can_adjust_shelf_notches: canAdjustShelfNotches,
        can_add_or_delete_shelves: canAddOrDeleteShelves,
        shelves_same_height_as_base_pog: shelvesSameHeightAsBasePog,
        vertical_overlap: verticalOverlap,
        merged_shelf_width_tolerance_pct: mergedShelfWidthTolerancePct,
        vertical_split_section: verticalSplitSection,
      },
      range: {
        can_add_non_core_range_products: canAddNonCoreRangeProducts,
        can_apply_squeeze_to_placed_products: canApplySqueezeToPlacedProducts,
      },
      merchandising: {
        vertical_blocking_mode: verticalBlockingMode,
        can_break_cases_trays_if_core_range_not_fit: canBreakCasesTraysIfCoreRangeNotFit,
        can_fill_extra_space_next_to_cases_trays_with_loose_products:
          canFillExtraSpaceNextToCasesTraysWithLooseProducts,
        how_should_loose_products_be_placed_with_respect_to_their_cases_trays:
          howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
        leverage_alternative_orientations: leverageAlternativeOrientations,
        extra_facing_marginal_value: extraFacingMarginalValue ?? 0,
        snaking: snaking,
        cdt_stacking: cdtStacking,
        items_squeeze_option: itemsSqueezeOption,
      },
      high_density_mode: {
        enabled: highDensityEnabled,
        place_all_variants: placeAllVariants,
        snaking_strategy: snakingStrategy,
        variant_contiguous: variantContiguous,
        allow_breaking_variant_sequencing: allowBreakingVariantSequencing,
        vertical_blocking_mode: ameVerticalBlockingMode,
        strict_rectangles: strictRectangles,
      },
      high_density_building_blocks_mode: {
        enabled: buildingBlocksEnabled,
        direction: buildingBlocksDirection,
        start_from: startFromType,
      },
      mudmap_level: mudmapLevel,
      optimize_on: optimizeOn,
      relax_factors: {
        space_mix: spaceMix,
        either_or: eitherOr,
        both_or_none: bothOrNone,
        prereq: preReq,
        shelf_item_count: shelfItemCount,
        fixed_notch_num: fixedNotchNum,
        finger_space: fingerSpace,
        units_not_enough_dos: unitsNotEnoughDos,
        units_not_enough_mos: unitsNotEnoughMos,
        units_too_many: unitsTooMany,
        facings_not_enough: facingsNotEnough,
        facings_too_many: facingsTooMany,
        rows_not_enough: rowsNotEnough,
        min_width: minWidth,
        min_distribution: minDistribution,
        width_slack: widthSlack,
        cdt1_contiguous: cdt1Contiguous,
        cdt2_contiguous: cdt2Contiguous,
        cdt3_contiguous: cdt3Contiguous,
        cdt1_order: cdt1Order,
        cdt2_order: cdt2Order,
        cdt3_order: cdt3Order,
        item_id_order: itemIdOrder,
        item_span: itemSpan,
        cdt1_span: cdt1Span,
        cdt2_span: cdt2Span,
        cdt3_span: cdt3Span,
        secondary_orientation: secondaryOrientation,
        shelf_segment_space: shelfSegmentSpace,
      },
      ...(mode !== "base" && {
        mudmap_generation_penalty_multiplier: {
          totalSpaceSlack,
          cdt0SectionsSpanning,
          cdt0ShelvesSpanning,
          cdt2SectionsSpanning,
          cdt2ShelvesSpanning,
          cdt0RoundingSlack,
          cdt2RoundingSlack,
          cdt1BreakingSequencing,
          cdt2BreakingSequencing,
          cdtSpaceMixSlack,
          cdt0ShelvesMisaligned,
          itemWidthSatisfied,
          cdt0ShelfSpaceMisalignment,
          cdt2ShelfSpaceMisalignment,
          verticalBlockShelfPresence,
          cdt0CellXMisalignment,
          cdt2CellXMisalignment,
        },
      }),
      // Omitted, not defaulted, while the gate is off: the tab is hidden, so
      // persisting rules nobody can see or edit would hand them to whoever
      // switches the setting on later. Optional on the contract, and
      // `configTemplate.ts` already accepts a config without it.
      weight_rules: isShelfWeightEnabled
        ? {
            default_shelf_weight_limit: defaultShelfWeightLimit,
            heavy_rule: heavyRule,
            resolve_overweight_shelves: resolveOverweightShelves,
            swap_heavy_brands_below_max_height: swapHeavyBrandsBelowMaxHeight,
            heavy_brand_block_level: heavyBrandBlockLevel,
            swap_fill_floor_pct: swapFillFloorPct,
            // Not user-facing: the backend forces it on whenever the merchant
            // gate is enabled, since the report is the screen's only source of
            // shelf limits.
            report: false,
          }
        : undefined,
    };
  };

  const withGenerationLock = async (run: () => Promise<void>) => {
    if (isGeneratingRef.current) {
      return;
    }

    isGeneratingRef.current = true;
    setIsGenerating(true);

    try {
      await run();
    } finally {
      isGeneratingRef.current = false;
      if (isMountedRef.current) {
        setIsGenerating(false);
      }
    }
  };

  const handleGenerationError = (error: unknown) => {
    toast({
      title: "Failed to start generation",
      error,
    });
  };

  const runBaseGeneration = async ({
    merchflowId,
    pog,
    s2SolutionId,
  }: StorePlanogramRulesModalBaseProps) => {
    const response = await postStartMerchflowBasePogApi(merchflowId, {
      s2_solution_id: s2SolutionId,
      s2_solution: pog,
      psp_rules: buildPspRules(),
    });

    if (!response?.base_pog_id) {
      return;
    }

    navigate(
      routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
        merchflowId,
        basePogId: response.base_pog_id,
      }),
    );
    closeModal();
  };

  const runStoreGeneration = async ({
    basePogId,
    merchflowId,
  }: StorePlanogramRulesModalStoreProps) => {
    const storePogTriggerId = copyToStores
      ? await postMerchflowStartStorePlanogramsCopyToStoresGenerationApi({
          base_pog_id: basePogId,
        })
      : await postMerchflowStartStorePlanogramsGenerationApi({
          base_pog_id: basePogId,
          psp_rules: buildPspRules(),
        });

    if (!storePogTriggerId) {
      return;
    }

    closeModal();

    const navigateToStorePlanograms = () => {
      navigate(
        routeToUrl(
          pages.merchflows_merchflowId_StoresAll,
          {
            merchflowId,
          },
          {
            store_pog_trigger_id: storePogTriggerId,
          },
        ),
      );
    };

    for (let attempt = 0; attempt < MAX_STORE_GENERATION_POLL_ATTEMPTS; attempt += 1) {
      const storeAllResponse = await getMerchflowStoresAllApi(merchflowId, {
        page: 1,
        size: 8,
        store_pog_trigger_id: storePogTriggerId,
        viewMode: "list",
      });

      if (storeAllResponse?.data?.length) {
        navigateToStorePlanograms();
        return;
      }

      if (attempt < MAX_STORE_GENERATION_POLL_ATTEMPTS - 1) {
        await wait(STORE_GENERATION_POLL_INTERVAL_MS);
      }
    }

    toast({
      title: "Generation is taking longer than expected",
      message: "You can check the status later on the Store Planograms page.",
      type: "WARNING",
    });
    navigateToStorePlanograms();
  };

  const startBaseGeneration = async () => {
    if (mode !== "base") {
      return;
    }

    await withGenerationLock(() => runBaseGeneration(props));
  };

  const startStoreGeneration = async () => {
    if (mode !== "store") {
      return;
    }

    await withGenerationLock(() => runStoreGeneration(props));
  };

  const startStoreGenerationAfterFavorite = async () => {
    if (mode !== "store") {
      return;
    }

    await withGenerationLock(async () => {
      await postMerchflowBasePog_basePogId_StatusApi({
        is_favourite: [props.basePogId],
      });
      await runStoreGeneration(props);
    });
  };

  const handleBaseGenerateClick = () => {
    startBaseGeneration().catch(handleGenerationError);
  };

  const handleStoreGenerateClick = () => {
    if (mode !== "store") {
      return;
    }

    const { isBasePogFavorited } = props;
    if (isBasePogFavorited) {
      startStoreGeneration().catch(handleGenerationError);
      return;
    }

    openConfirmModal({
      title: "Start Generating Store Planograms?",
      description:
        "Only store planograms of favourited base planograms are available for approval in the Store Review step of your flow review.",
      buttons: [
        { id: "back", label: "Back", color: "primary", variant: "inverted" },
        {
          id: "only-generate",
          label: "Only Generate",
          color: "yellow",
          onClick: () => {
            startStoreGeneration().catch(handleGenerationError);
          },
        },
        {
          id: "favorite-and-generate",
          label: "Favorite and Generate",
          color: "primary",
          onClick: () => {
            startStoreGenerationAfterFavorite().catch(handleGenerationError);
          },
        },
      ],
    });
  };

  const closeModal = () => {
    _closeModal("ModalStorePlanogramRules");
  };

  const isBaseMode = mode === "base";
  const isUhdModeEnabled = highDensityEnabled || buildingBlocksEnabled;
  const weightTab: RulesTabOption[] = isShelfWeightEnabled
    ? [{ label: t("planogramRules.weightRules.tab"), value: "WEIGHT" }]
    : [];

  const tabs: RulesTabOption[] = [
    { label: "General", value: "GENERAL" },
    { label: "Auto-Mudmap Engine (UHD Mode)", value: "UHD" },
    { label: "Optimization Options", value: "OPTIMIZATION" },
    ...weightTab,
  ];

  return (
    <Modal
      title={isBaseMode ? "Base Planogram Rules" : "Store Planogram Rules"}
      name="ModalStorePlanogramRules"
      width="500px"
      height="700px"
    >
      <Flex minHeight="0" flexGrow column gap="10px">
        <Flex minHeight="0" flexGrow column gap="20px" padding="10px">
          {showEngineNeededNotice && (
            <Notice text="Auto-Mudmap is off. Vertical blocking remains enabled, but the planogram will be generated without a mudmap.">
              <Button
                color="primary"
                variant="inverted"
                onClick={() => {
                  handleVerticalBlockingModeChange(false);
                }}
              >
                Turn vertical blocking off
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  handleHighDensityEnabledChange(true);
                }}
              >
                Turn the engine on
              </Button>
            </Notice>
          )}

          <Tabs
            tabs={tabs}
            value={tab}
            setValue={setTab}
            isDisabled={copyToStores && !isBaseMode}
          />

          <Scroller gutter="stable">
            {tab === "GENERAL" && (
              <GeneralPlanogramRules
                isDisabled={copyToStores && !isBaseMode}
                // Fixture
                canAdjustShelfNotches={canAdjustShelfNotches}
                setCanAdjustShelfNotches={setCanAdjustShelfNotches}
                canAddOrDeleteShelves={canAddOrDeleteShelves}
                setCanAddOrDeleteShelves={setCanAddOrDeleteShelves}
                shelvesSameHeightAsBasePog={shelvesSameHeightAsBasePog}
                setShelvesSameHeightAsBasePog={setShelvesSameHeightAsBasePog}
                verticalOverlap={verticalOverlap}
                setVerticalOverlap={setVerticalOverlap}
                mergedShelfWidthTolerancePct={mergedShelfWidthTolerancePct}
                setMergedShelfWidthTolerancePct={setMergedShelfWidthTolerancePct}
                // Range
                canAddNonCoreRangeProducts={canAddNonCoreRangeProducts}
                setCanAddNonCoreRangeProducts={setCanAddNonCoreRangeProducts}
                canApplySqueezeToPlacedProducts={canApplySqueezeToPlacedProducts}
                setCanApplySqueezeToPlacedProducts={setCanApplySqueezeToPlacedProducts}
                // Merchandising
                verticalBlockingMode={verticalBlockingMode}
                setVerticalBlockingMode={handleVerticalBlockingModeChange}
                canBreakCasesTraysIfCoreRangeNotFit={canBreakCasesTraysIfCoreRangeNotFit}
                setCanBreakCasesTraysIfCoreRangeNotFit={setCanBreakCasesTraysIfCoreRangeNotFit}
                canFillExtraSpaceNextToCasesTraysWithLooseProducts={
                  canFillExtraSpaceNextToCasesTraysWithLooseProducts
                }
                setCanFillExtraSpaceNextToCasesTraysWithLooseProducts={
                  setCanFillExtraSpaceNextToCasesTraysWithLooseProducts
                }
                howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays={
                  howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays
                }
                setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays={
                  setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays
                }
                leverageAlternativeOrientations={leverageAlternativeOrientations}
                setLeverageAlternativeOrientations={setLeverageAlternativeOrientations}
                extraFacingMarginalValue={extraFacingMarginalValue ?? 0}
                setExtraFacingMarginalValue={setExtraFacingMarginalValue}
                snaking={snaking}
                setSnaking={setSnaking}
                cdtStacking={cdtStacking}
                setCdtStacking={setCdtStacking}
                itemsSqueezeOption={itemsSqueezeOption}
                setItemsSqueezeOption={setItemsSqueezeOption}
              />
            )}

            {tab === "UHD" && (
              <>
                {!isBaseMode && (
                  <>
                    <UhdPlanogramRules
                      isDisabled={copyToStores && !isBaseMode}
                      merchflowId={merchflowId}
                      // High Density Mode
                      highDensityEnabled={highDensityEnabled}
                      setHighDensityEnabled={handleHighDensityEnabledChange}
                      placeAllVariants={placeAllVariants}
                      setPlaceAllVariants={setPlaceAllVariants}
                      snakingStrategy={snakingStrategy}
                      setSnakingStrategy={setSnakingStrategy}
                      variantContiguous={variantContiguous}
                      setVariantContiguous={setVariantContiguous}
                      allowBreakingVariantSequencing={allowBreakingVariantSequencing}
                      setAllowBreakingVariantSequencing={setAllowBreakingVariantSequencing}
                      ameVerticalBlockingMode={ameVerticalBlockingMode}
                      strictRectangles={strictRectangles}
                      setStrictRectangles={setStrictRectangles}
                      verticalSplitSection={verticalSplitSection}
                      setVerticalSplitSection={setVerticalSplitSection}
                      // High Density Building Blocks Mode
                      buildingBlocksEnabled={buildingBlocksEnabled}
                      setBuildingBlocksEnabled={setBuildingBlocksEnabled}
                      buildingBlocksDirection={buildingBlocksDirection}
                      setBuildingBlocksDirection={setBuildingBlocksDirection}
                      startFromType={startFromType}
                      setStartFromType={setStartFromType}
                    />
                    <MudmapOptimizationOptions
                      isDisabled={copyToStores || !isUhdModeEnabled}
                      mudmapOptimizationEnabled={mudmapOptimizationEnabled}
                      setMudmapOptimizationEnabled={setMudmapOptimizationEnabled}
                      totalSpaceSlack={totalSpaceSlack}
                      setTotalSpaceSlack={setTotalSpaceSlack}
                      cdt0SectionsSpanning={cdt0SectionsSpanning}
                      setCdt0SectionsSpanning={setCdt0SectionsSpanning}
                      cdt0ShelvesSpanning={cdt0ShelvesSpanning}
                      setCdt0ShelvesSpanning={setCdt0ShelvesSpanning}
                      cdt2SectionsSpanning={cdt2SectionsSpanning}
                      setCdt2SectionsSpanning={setCdt2SectionsSpanning}
                      cdt2ShelvesSpanning={cdt2ShelvesSpanning}
                      setCdt2ShelvesSpanning={setCdt2ShelvesSpanning}
                      cdt0RoundingSlack={cdt0RoundingSlack}
                      setCdt0RoundingSlack={setCdt0RoundingSlack}
                      cdt2RoundingSlack={cdt2RoundingSlack}
                      setCdt2RoundingSlack={setCdt2RoundingSlack}
                      cdt1BreakingSequencing={cdt1BreakingSequencing}
                      setCdt1BreakingSequencing={setCdt1BreakingSequencing}
                      cdt2BreakingSequencing={cdt2BreakingSequencing}
                      setCdt2BreakingSequencing={setCdt2BreakingSequencing}
                      cdtSpaceMixSlack={cdtSpaceMixSlack}
                      setCdtSpaceMixSlack={setCdtSpaceMixSlack}
                      cdt0ShelvesMisaligned={cdt0ShelvesMisaligned}
                      setCdt0ShelvesMisaligned={setCdt0ShelvesMisaligned}
                      itemWidthSatisfied={itemWidthSatisfied}
                      setItemWidthSatisfied={setItemWidthSatisfied}
                      cdt0ShelfSpaceMisalignment={cdt0ShelfSpaceMisalignment}
                      setCdt0ShelfSpaceMisalignment={setCdt0ShelfSpaceMisalignment}
                      cdt2ShelfSpaceMisalignment={cdt2ShelfSpaceMisalignment}
                      setCdt2ShelfSpaceMisalignment={setCdt2ShelfSpaceMisalignment}
                      verticalBlockShelfPresence={verticalBlockShelfPresence}
                      setVerticalBlockShelfPresence={setVerticalBlockShelfPresence}
                      cdt0CellXMisalignment={cdt0CellXMisalignment}
                      setCdt0CellXMisalignment={setCdt0CellXMisalignment}
                      cdt2CellXMisalignment={cdt2CellXMisalignment}
                      setCdt2CellXMisalignment={setCdt2CellXMisalignment}
                      onReset={resetMudmapOptimizationOptions}
                    />
                  </>
                )}
              </>
            )}

            {tab === "OPTIMIZATION" && (
              <OptimizationOptionsRules
                isDisabled={copyToStores && !isBaseMode}
                spaceMix={spaceMix}
                setSpaceMix={setSpaceMix}
                eitherOr={eitherOr}
                setEitherOr={setEitherOr}
                bothOrNone={bothOrNone}
                setBothOrNone={setBothOrNone}
                preReq={preReq}
                setPreReq={setPreReq}
                shelfItemCount={shelfItemCount}
                setShelfItemCount={setShelfItemCount}
                fixedNotchNum={fixedNotchNum}
                setFixedNotchNum={setFixedNotchNum}
                fingerSpace={fingerSpace}
                setFingerSpace={setFingerSpace}
                unitsNotEnoughDos={unitsNotEnoughDos}
                setUnitsNotEnoughDos={setUnitsNotEnoughDos}
                unitsNotEnoughMos={unitsNotEnoughMos}
                setUnitsNotEnoughMos={setUnitsNotEnoughMos}
                unitsTooMany={unitsTooMany}
                setUnitsTooMany={setUnitsTooMany}
                facingsNotEnough={facingsNotEnough}
                setFacingsNotEnough={setFacingsNotEnough}
                facingsTooMany={facingsTooMany}
                setFacingsTooMany={setFacingsTooMany}
                rowsNotEnough={rowsNotEnough}
                setRowsNotEnough={setRowsNotEnough}
                minWidth={minWidth}
                setMinWidth={setMinWidth}
                minDistribution={minDistribution}
                setMinDistribution={setMinDistribution}
                widthSlack={widthSlack}
                setWidthSlack={setWidthSlack}
                cdt1Contiguous={cdt1Contiguous}
                setCdt1Contiguous={setCdt1Contiguous}
                cdt2Contiguous={cdt2Contiguous}
                setCdt2Contiguous={setCdt2Contiguous}
                cdt3Contiguous={cdt3Contiguous}
                setCdt3Contiguous={setCdt3Contiguous}
                cdt1Order={cdt1Order}
                setCdt1Order={setCdt1Order}
                cdt2Order={cdt2Order}
                setCdt2Order={setCdt2Order}
                cdt3Order={cdt3Order}
                setCdt3Order={setCdt3Order}
                itemIdOrder={itemIdOrder}
                setItemIdOrder={setItemIdOrder}
                itemSpan={itemSpan}
                setItemSpan={setItemSpan}
                cdt1Span={cdt1Span}
                setCdt1Span={setCdt1Span}
                cdt2Span={cdt2Span}
                setCdt2Span={setCdt2Span}
                cdt3Span={cdt3Span}
                setCdt3Span={setCdt3Span}
                secondaryOrientation={secondaryOrientation}
                setSecondaryOrientation={setSecondaryOrientation}
                shelfSegmentSpace={shelfSegmentSpace}
                setShelfSegmentSpace={setShelfSegmentSpace}
              />
            )}

            {tab === "WEIGHT" && (
              <WeightRules
                isDisabled={copyToStores && !isBaseMode}
                defaultShelfWeightLimit={defaultShelfWeightLimit}
                setDefaultShelfWeightLimit={setDefaultShelfWeightLimit}
                heavyRule={heavyRule}
                setHeavyRule={setHeavyRule}
                resolveOverweightShelves={resolveOverweightShelves}
                setResolveOverweightShelves={setResolveOverweightShelves}
                swapHeavyBrandsBelowMaxHeight={swapHeavyBrandsBelowMaxHeight}
                setSwapHeavyBrandsBelowMaxHeight={setSwapHeavyBrandsBelowMaxHeight}
                heavyBrandBlockLevel={heavyBrandBlockLevel}
                setHeavyBrandBlockLevel={setHeavyBrandBlockLevel}
                swapFillFloorPct={swapFillFloorPct}
                setSwapFillFloorPct={setSwapFillFloorPct}
              />
            )}
          </Scroller>
        </Flex>

        <Flex
          column
          background={Color.lightGray}
          borderTop={`1px solid ${Color.lightGrayHover}`}
          padding="20px"
          gap="20px"
        >
          <PlanogramMudmapLevel
            mudmapLevel={mudmapLevel}
            setMudmapLevel={setMudmapLevel}
            isDisabled={copyToStores}
          />

          <PlanogramOptimizeOn
            optimizeOn={optimizeOn}
            setOptimizeOn={setOptimizeOn}
            isDisabled={copyToStores}
          />

          <HorizontalDivider />

          <Toggle
            label="Copy Base Planogram to Stores"
            value={copyToStores}
            setValue={(checked) => {
              setCopyToStores(checked);
            }}
          />

          <Flex width="100%" justify="between" align="center">
            <Flex gap="8px">
              <Button
                color="primary"
                variant="inverted"
                isDisabled={copyToStores && !isBaseMode}
                onClick={handleImportClick}
              >
                Import
              </Button>
              <Button
                color="primary"
                variant="inverted"
                isDisabled={copyToStores && !isBaseMode}
                onClick={handleExportConfig}
              >
                Export
              </Button>
            </Flex>

            <Flex gap="20px">
              <Button color="red" variant="inverted" onClick={closeModal} isDisabled={false}>
                Cancel
              </Button>
              {canCreate && (
                <Button
                  color="primary"
                  onClick={isBaseMode ? handleBaseGenerateClick : handleStoreGenerateClick}
                  isDisabled={isGenerating}
                  isLoading={isGenerating}
                >
                  {isBaseMode ? "Generate Base Planogram" : "Generate Store Planograms"}
                </Button>
              )}
            </Flex>
          </Flex>

          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleImportConfig}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};
